// src\themes\lino\elements\comment\CommentForm.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import Link from 'next/link';

import TextareaInput from '@/components/account/input/TextareaInput';
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")
import { useAuth } from '@/contexts/authContext';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { postCommentProductById } from '@/core/lib/api/main/main-shop-product';
import {
  CommentFormData,
  getCommentFormSchema,
} from '@/core/schemas/commentSchema';

interface CommentFormProps {
  productId: number;
}

export default function CommentForm({ productId }: CommentFormProps) {
  const {
    state: { isAuthenticated },
  } = useAuth();
  const { dictionary } = useDictionary();
  const commentSchema = getCommentFormSchema();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
  });

  const onSubmit = async (data: CommentFormData) => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const result = await postCommentProductById(productId, {
        content: data.content,
      });

      if (result.status === 'success') {
        toast.success(result.message);
        reset();
      } else if (result.status === 'error') {
        const contentError = result.data?.errors?.content;

        setError('content', { type: 'server', message: contentError });
        toast.error(result.message);
      } else {
        toast.error(result.message || dictionary.errors.createError);
      }
    } catch (err) {
      console.error(err);
      setError('content', { type: 'server', message: dictionary.common?.error });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="my-8 p-10 rounded-2xl flex items-center justify-center flex-col border border-primary-200">
        <p className="mb-5 text-[13px] text-primary-600">{dictionary.ui?.pages.alertforcomment}</p>
        <CustomButton size="lg" asChild>
          <Link href="/login">
            {dictionary.forms?.login} | {dictionary.forms?.signin}
          </Link>
        </CustomButton>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-3 my-8"
    >
      <TextareaInput
        label={dictionary.forms?.content}
        register={register}
        name="content"
        placeholder=""
        disabled={isSubmitting}
        inputClassName="!rounded-2xl !border-primary-200 focus:!border-primary-900"
        errors={errors}
      />
      <CustomButton
        type="submit"
        disabled={isSubmitting}
        size="sm"
        className="w-fit"
      >
        {isSubmitting ? (
          <Loader2 className="animate-spin size-4" />
        ) : (
          dictionary.common?.submit
        )}
      </CustomButton>
    </form>
  );
}